/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Compresses and resizes an image file client-side.
 * Returns the processed image as a Base64 string.
 * 
 * @param file The input image File
 * @param maxWidth Maximum width in pixels
 * @param maxHeight Maximum height in pixels
 * @param quality JPEG/WebP quality (0 to 1)
 * @returns Promise resolving to Base64 string
 */
export const compressAndResizeImage = (
  file: File,
  maxWidth: number = 2500,
  maxHeight: number = 2500,
  quality: number = 0.95
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to Base64
        // Use JPEG for photos (better compression) unless it's a PNG with transparency
        const outputType = file.type === 'image/png' || file.type === 'image/webp' ? file.type : 'image/jpeg';
        
        // If it's a PNG, we might want to keep it as PNG to preserve transparency, 
        // but PNG doesn't support quality parameter in toDataURL. 
        // If file size is critical, we could convert to WebP which supports transparency and quality.
        const finalType = 'image/webp'; // WebP is widely supported now and efficient
        
        try {
            const base64 = canvas.toDataURL(finalType, quality);
            resolve(base64);
        } catch (e) {
            // Fallback to original type if WebP fails (unlikely in modern browsers)
             const fallbackBase64 = canvas.toDataURL(file.type, quality);
             resolve(fallbackBase64);
        }
      };
      
      img.onerror = (error) => reject(error);
    };
    
    reader.onerror = (error) => reject(error);
  });
};
